import type {
  PolicySearchCriteria,
  PolicySearchResponse,
  Underwriter,
  UnderwriterDetails,
  UnderwriterNameOperator,
  PolicySearchCriteriaDetails,
  UnderWriterAndPolicyTypeDetails
} from "@/models/underwriter";
import ApiService from "./apiService";

type ApiResult<T> = { status: number; data: T };

function mapOperatorToServerLiteral(op: UnderwriterNameOperator): string {
  switch (op) {
    case "CONTAINS":
      return "Contains";
    case "NOT_CONTAINS":
      return "Does Not Contain";
    case "BEGINS_WITH":
      return "Begins With";
    case "EQUALS":
      return "Equals";
    default:
      return "Equals";
  }
}

function normalizeUnderwriter(x: any): Underwriter {
  const id = x?.UserId ?? x?.userId ?? x?.userid ?? x?.UserID ?? x?.userID ?? 0;
  const nm =
    x?.UnderwriterName ??
    x?.underwriterName ??
    x?.underWriterName ??
    x?.name ??
    x?.displayName ??
    "";
  return {
    UserId: Number(id),
    UnderwriterName: String(nm).trim() || null
  };
}

function normalizePolicy(x: any): PolicySearchResponse {
  return {
    PolicyId: Number(x?.PolicyId ?? x?.policyId ?? 0),
    PolicyNum: String(x?.PolicyNum ?? x?.policyNum ?? "") || null,
    PolicyType: String(x?.PolicyType ?? x?.policyType ?? "") || null,
    PolicyTxnNum: Number(x?.PolicyTxnNum ?? x?.policyTxnNum ?? 0),
    PolicyTermNum: Number(x?.PolicyTermNum ?? x?.policyTermNum ?? 0),
    PolicyTxnStatus: String(x?.PolicyTxnStatus ?? x?.policyTxnStatus ?? "") || null,
    TxnType: String(x?.TxnType ?? x?.txnType ?? "") || null,
    ProcessStatus: String(x?.ProcessStatus ?? x?.processStatus ?? "") || null,
    StateName: String(x?.StateName ?? x?.stateName ?? "") || null,
    InsuredFullName: String(x?.InsuredFullName ?? x?.insuredFullName ?? "") || null,
    AgencyFullName: String(x?.AgencyFullName ?? x?.agencyFullName ?? "") || null,
    AgencyAddress: String(x?.AgencyAddress ?? x?.agencyAddress ?? "") || null,
    AgencyCity: String(x?.AgencyCity ?? x?.agencyCity ?? "") || null,
    AgencyState: String(x?.AgencyState ?? x?.agencyState ?? "") || null,
    AgencyZipCode: String(x?.AgencyZipCode ?? x?.agencyZipCode ?? "") || null,
    PolicyEff: String(x?.PolicyEff ?? x?.policyEff ?? "") || null,
    PolicyExp: String(x?.PolicyExp ?? x?.policyExp ?? "") || null
  };
}

function toNullIfEmpty(v: any) {
  const s = String(v ?? "").trim();
  return s.length > 0 ? s : null;
}

function toNumberOrZero(v: any) {
  const n = Number(v ?? 0);
  return Number.isFinite(n) ? n : 0;
}

function toArrayOrNull(v: any): number[] | null {
  if (!Array.isArray(v)) return null;
  const arr = v.map((x) => Number(x)).filter((n) => Number.isFinite(n));
  return arr.length > 0 ? arr : null;
}

export const underwriterService = {
  async searchSourceUnderwriters(
    operator: UnderwriterNameOperator,
    name?: string | null
  ): Promise<ApiResult<Underwriter[]>> {
    const payload = {
      query: mapOperatorToServerLiteral(operator),
      name: String(name ?? "").trim()
    };
    const res = await ApiService.post<any[]>(
      `/Underwriter/source/search`,
      payload
    );
    const data = (res.data ?? []).map(normalizeUnderwriter);
    return { status: res.status, data };
  },

  async getPolicySearchCriteria(): Promise<ApiResult<PolicySearchCriteriaDetails>> {
    const res = await ApiService.get<PolicySearchCriteriaDetails>(
      `/Underwriter/getpolicysearchcriteria`
    );
    return { status: res.status, data: res.data };
  },

  async getUserAndPolicyType(subCompanyCode: string): Promise<ApiResult<UnderWriterAndPolicyTypeDetails>> {
    const code = encodeURIComponent(String(subCompanyCode ?? "").trim());
    const res = await ApiService.get<UnderWriterAndPolicyTypeDetails>(
      `/Underwriter/getuserandpolicytype/${code}`
    );
    const raw: any = res.data ?? {};
    const data: UnderWriterAndPolicyTypeDetails = {
      PolicyTypeList: Array.isArray(raw?.PolicyTypeList) ? raw.PolicyTypeList : raw?.policyTypeList ?? null,
      UnderwriterList: Array.isArray(raw?.UnderwriterList)
        ? raw.UnderwriterList.map(normalizeUnderwriter)
        : Array.isArray(raw?.underwriterList)
        ? raw.underwriterList.map(normalizeUnderwriter)
        : null
    };
    return { status: res.status, data };
  },

  async policySearch(criteria: PolicySearchCriteria): Promise<ApiResult<PolicySearchResponse[]>> {
    const payload: PolicySearchCriteria = {
      SourceUnderwriterId: toNumberOrZero(criteria.SourceUnderwriterId),
      SubCompanyCode: toNullIfEmpty(criteria.SubCompanyCode),
      PolicyTypeId: toNumberOrZero(criteria.PolicyTypeId),
      ProcessStatusCode: toNullIfEmpty(criteria.ProcessStatusCode),
      TransactionTypeCode: toNullIfEmpty(criteria.TransactionTypeCode),
      TransactionStatusCode: toNullIfEmpty(criteria.TransactionStatusCode),
      EffectiveDate: toNullIfEmpty(criteria.EffectiveDate),
      ExpieryDate: toNullIfEmpty(criteria.ExpieryDate),
      PolicyStateId: toNumberOrZero(criteria.PolicyStateId)
    };
    const res = await ApiService.post<any[]>(`/Underwriter/policysearch`, payload);
    const data = (res.data ?? []).map(normalizePolicy);
    return { status: res.status, data };
  },

  async confirmUpdate(payload: UnderwriterDetails): Promise<ApiResult<any>> {
    const body: UnderwriterDetails = {
      SourceUnderwriterId: toNumberOrZero(payload.SourceUnderwriterId),
      DestinationUnderwriterId: toNumberOrZero(payload.DestinationUnderwriterId),
      SubCompanyCode: toNullIfEmpty(payload.SubCompanyCode),
      PolicyTypeId: toNumberOrZero(payload.PolicyTypeId),
      PolicyIds: toArrayOrNull(payload.PolicyIds)
    };
    const res = await ApiService.put(`/Underwriter`, body);
    return { status: res.status, data: res.data };
  },

  async checkSecurityPermission(): Promise<ApiResult<any>> {
    const res = await ApiService.get(`/Underwriter/getchecksecuritypermission`);
    return { status: res.status, data: res.data };
  }
};
